<script>
import { mapGetters } from 'vuex';
import { useVuelidate } from '@vuelidate/core';
import { useAlert } from 'dashboard/composables';
import { required } from '@vuelidate/validators';
import router from '../../../../index';
import { isPhoneE164OrEmpty } from 'shared/helpers/Validators';

import NextButton from 'dashboard/components-next/button/Button.vue';

export default {
  components: {
    NextButton,
  },
  setup() {
    return { v$: useVuelidate() };
  },
  data() {
    return {
      inboxName: '',
      phoneNumber: '',
      apiUrl: '',
      apiKey: '',
      forwardingWebhookUrl: '',
      handleGroups: false,
    };
  },
  computed: {
    ...mapGetters({ uiFlags: 'inboxes/getUIFlags' }),
  },
  validations: {
    inboxName: { required },
    phoneNumber: { required, isPhoneE164OrEmpty },
    apiUrl: { required },
    apiKey: { required },
  },
  methods: {
    async createChannel() {
      this.v$.$touch();
      if (this.v$.$invalid) {
        return;
      }

      try {
        const whatsappChannel = await this.$store.dispatch(
          'inboxes/createChannel',
          {
            name: this.inboxName?.trim(),
            channel: {
              type: 'whatsapp',
              phone_number: this.phoneNumber,
              provider: 'zuckzapgo',
              provider_config: {
                api_url: this.apiUrl?.trim().replace(/\/+$/, ''),
                api_key: this.apiKey?.trim(),
                forwarding_webhook_url: this.forwardingWebhookUrl?.trim(),
                handle_groups: this.handleGroups,
              },
            },
          }
        );

        router.replace({
          name: 'settings_inboxes_add_agents',
          params: {
            page: 'new',
            inbox_id: whatsappChannel.id,
          },
        });
      } catch (error) {
        useAlert(
          error.message ||
            this.$t('INBOX_MGMT.ADD.WHATSAPP.ZUCKZAPGO.API.ERROR_MESSAGE')
        );
      }
    },
  },
};
</script>

<template>
  <form class="flex flex-col flex-wrap mx-0" @submit.prevent="createChannel()">
    <div class="p-4 mb-4 rounded-md border border-n-weak bg-n-alpha-1">
      <p class="text-sm text-n-slate-11">
        {{ $t('INBOX_MGMT.ADD.WHATSAPP.ZUCKZAPGO.HELP') }}
      </p>
    </div>

    <div class="flex-shrink-0 flex-grow-0">
      <label :class="{ error: v$.inboxName.$error }">
        {{ $t('INBOX_MGMT.ADD.WHATSAPP.INBOX_NAME.LABEL') }}
        <input
          v-model="inboxName"
          type="text"
          :placeholder="$t('INBOX_MGMT.ADD.WHATSAPP.INBOX_NAME.PLACEHOLDER')"
          @blur="v$.inboxName.$touch"
        />
        <span v-if="v$.inboxName.$error" class="message">
          {{ $t('INBOX_MGMT.ADD.WHATSAPP.INBOX_NAME.ERROR') }}
        </span>
      </label>
    </div>

    <div class="flex-shrink-0 flex-grow-0">
      <label :class="{ error: v$.phoneNumber.$error }">
        {{ $t('INBOX_MGMT.ADD.WHATSAPP.PHONE_NUMBER.LABEL') }}
        <input
          v-model="phoneNumber"
          type="text"
          :placeholder="$t('INBOX_MGMT.ADD.WHATSAPP.PHONE_NUMBER.PLACEHOLDER')"
          @blur="v$.phoneNumber.$touch"
        />
        <span v-if="v$.phoneNumber.$error" class="message">
          {{ $t('INBOX_MGMT.ADD.WHATSAPP.PHONE_NUMBER.ERROR') }}
        </span>
      </label>
    </div>

    <div class="flex-shrink-0 flex-grow-0">
      <label :class="{ error: v$.apiUrl.$error }">
        {{ $t('INBOX_MGMT.ADD.WHATSAPP.ZUCKZAPGO.API_URL.LABEL') }}
        <input
          v-model="apiUrl"
          type="text"
          :placeholder="
            $t('INBOX_MGMT.ADD.WHATSAPP.ZUCKZAPGO.API_URL.PLACEHOLDER')
          "
          @blur="v$.apiUrl.$touch"
        />
        <span v-if="v$.apiUrl.$error" class="message">
          {{ $t('INBOX_MGMT.ADD.WHATSAPP.ZUCKZAPGO.API_URL.ERROR') }}
        </span>
      </label>
    </div>

    <div class="flex-shrink-0 flex-grow-0">
      <label :class="{ error: v$.apiKey.$error }">
        {{ $t('INBOX_MGMT.ADD.WHATSAPP.ZUCKZAPGO.API_KEY.LABEL') }}
        <input
          v-model="apiKey"
          type="password"
          :placeholder="
            $t('INBOX_MGMT.ADD.WHATSAPP.ZUCKZAPGO.API_KEY.PLACEHOLDER')
          "
          @blur="v$.apiKey.$touch"
        />
        <span v-if="v$.apiKey.$error" class="message">
          {{ $t('INBOX_MGMT.ADD.WHATSAPP.ZUCKZAPGO.API_KEY.ERROR') }}
        </span>
      </label>
    </div>

    <div class="flex-shrink-0 flex-grow-0">
      <label>
        {{ $t('INBOX_MGMT.ADD.WHATSAPP.ZUCKZAPGO.FORWARD_URL.LABEL') }}
        <input
          v-model="forwardingWebhookUrl"
          type="text"
          :placeholder="
            $t('INBOX_MGMT.ADD.WHATSAPP.ZUCKZAPGO.FORWARD_URL.PLACEHOLDER')
          "
        />
        <p class="mt-1 text-xs text-n-slate-11">
          {{ $t('INBOX_MGMT.ADD.WHATSAPP.ZUCKZAPGO.FORWARD_URL.HELP') }}
        </p>
      </label>
    </div>

    <div class="flex-shrink-0 flex-grow-0 mb-4">
      <label class="flex gap-2 items-center text-sm text-n-slate-12">
        <input v-model="handleGroups" type="checkbox" class="!mb-0" />
        {{ $t('INBOX_MGMT.ADD.WHATSAPP.ZUCKZAPGO.HANDLE_GROUPS') }}
      </label>
    </div>

    <div class="w-full">
      <NextButton
        :is-loading="uiFlags.isCreating"
        type="submit"
        solid
        blue
        :label="$t('INBOX_MGMT.ADD.WHATSAPP.SUBMIT_BUTTON')"
      />
    </div>
  </form>
</template>
